import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Send, Copy as CopyIcon, Plus, Trash2, Eye, FileText, Save } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Field } from "./Fields";
import { SingleRichEditor } from "./RichEditor";

const blankForm = () => ({
  name: "",
  title: "",
  emails: [""],
  phones: [""],
  reason: "",
  duration_minutes: 30,
  note: "",
});

/**
 * Full-featured appointment request modal: multi-email/phone, rich-text reason,
 * live preview, save-as-draft. Clicking the backdrop never closes the modal
 * — only the explicit × button or Vazgeç dismiss it. If `editingDraft` is
 * passed, we edit that draft and the primary action becomes "Taslağı Gönder".
 */
export default function AppointmentRequestModal({ open, onClose, editingDraft = null, onSaved }) {
  const [form, setForm] = useState(blankForm());
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [preview, setPreview] = useState(null); // {html, subject}
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingDraft) {
      setForm({
        name: editingDraft.name || "",
        title: editingDraft.title || "",
        emails: editingDraft.emails?.length ? editingDraft.emails : [editingDraft.email || ""],
        phones: editingDraft.phones?.length ? editingDraft.phones : [editingDraft.phone || ""],
        reason: editingDraft.reason || "",
        duration_minutes: editingDraft.duration_minutes || 30,
        note: editingDraft.admin_note || "",
      });
    } else {
      setForm(blankForm());
    }
    setSent(null);
    setPreview(null);
    setConfirmClose(false);
  }, [open, editingDraft]);

  if (!open) return null;

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updList = (k, i, v) => setForm((p) => ({ ...p, [k]: p[k].map((x, idx) => (idx === i ? v : x)) }));
  const addItem = (k) => setForm((p) => ({ ...p, [k]: [...p[k], ""] }));
  const removeItem = (k, i) =>
    setForm((p) => ({ ...p, [k]: p[k].length <= 1 ? p[k] : p[k].filter((_, idx) => idx !== i) }));

  const hasAnyData =
    !!form.name.trim() ||
    !!form.title.trim() ||
    form.emails.some((e) => e.trim()) ||
    form.phones.some((p) => p.trim()) ||
    !!form.reason.replace(/<[^>]+>/g, "").trim();

  const tryClose = () => {
    if (sent) return onClose();
    if (hasAnyData && !confirmClose) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const emailsClean = form.emails.map((e) => e.trim()).filter(Boolean);
  const phonesClean = form.phones.map((p) => p.trim()).filter(Boolean);

  const basePayload = () => ({
    name: form.name.trim(),
    title: form.title.trim(),
    emails: emailsClean,
    phones: phonesClean,
    reason: form.reason,
    duration_minutes: Number(form.duration_minutes) || 30,
    note: form.note.trim(),
  });

  const sendNow = async () => {
    if (!form.name.trim()) return toast.error("Ad Soyad zorunludur");
    if (!emailsClean.length) return toast.error("En az bir e-posta girin");
    const text = form.reason.replace(/<[^>]+>/g, "").trim();
    if (!text && !form.title.trim()) return toast.error("Başlık veya randevu nedeni girin");
    setBusy(true);
    try {
      let r;
      if (editingDraft) {
        await api.put(`/admin/appointments/${editingDraft.id}/draft`, basePayload());
        r = await api.post(`/admin/appointments/${editingDraft.id}/send`);
      } else {
        r = await api.post("/admin/appointments/request", basePayload());
      }
      setSent(r.data);
      toast.success("Randevu talebi gönderildi");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      if (editingDraft) {
        await api.put(`/admin/appointments/${editingDraft.id}/draft`, basePayload());
      } else {
        await api.post("/admin/appointments/draft", basePayload());
      }
      toast.success("Taslak kaydedildi");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/appointments/preview", {
        name: form.name || "Örnek Alıcı",
        title: form.title,
        reason: form.reason,
        duration_minutes: Number(form.duration_minutes) || 30,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(sent.link); toast.success("Bağlantı kopyalandı"); }
    catch { toast.error("Kopyalanamadı"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="appointment-request-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[94vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="font-serif text-xl font-bold">
            {sent ? "Talep gönderildi" : editingDraft ? "Taslağı Düzenle" : "Randevu Talep Et"}
          </h2>
          <button onClick={tryClose} className="text-stone-400 hover:text-stone-900 p-1 rounded hover:bg-stone-100" data-testid="appt-close" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {sent ? (
          <div className="p-6 space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 p-4 text-sm">
              <strong>Hazır!</strong> Alıcıya/alıcılara e-posta gönderildi. Yanıtı "Aksiyon Takibi" sekmesinde göreceksiniz.
            </div>
            <div>
              <div className="text-xs text-stone-500 mb-1 font-semibold">Benzersiz bağlantı</div>
              <div className="flex gap-2">
                <Input value={sent.link} readOnly className="font-mono text-xs" data-testid="appt-link" />
                <Button variant="outline" size="icon" onClick={copyLink} data-testid="appt-copy"><CopyIcon size={14} /></Button>
              </div>
              <p className="text-xs text-stone-400 mt-1">Bağlantı 7 gün geçerlidir. İsterseniz WhatsApp'tan da gönderebilirsiniz.</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose}>Kapat</Button>
            </div>
          </div>
        ) : preview ? (
          <div className="p-6 space-y-4">
            <div className="rounded-lg bg-stone-50 border p-3 text-sm">
              <div className="text-stone-500 text-xs uppercase tracking-wider font-semibold mb-1">Konu</div>
              <div className="font-semibold text-stone-900">{preview.subject}</div>
            </div>
            <iframe
              title="Önizleme"
              className="w-full h-[520px] rounded-lg border bg-white"
              srcDoc={preview.html}
              data-testid="appt-preview-frame"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="appt-preview-back">Düzenle</Button>
              <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="appt-save-draft">
                <Save size={14} className="mr-1" /> Taslağa Kaydet
              </Button>
              <Button onClick={sendNow} disabled={busy} data-testid="appt-submit">
                <Send size={14} className="mr-1" /> Gönder
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Ad Soyad" hint="Alıcının adı">
                <Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="appt-name" />
              </Field>
              <Field label="Tahmini süre (dk)">
                <Input type="number" min={5} max={480} value={form.duration_minutes}
                  onChange={(e) => upd("duration_minutes", e.target.value)} data-testid="appt-duration" />
              </Field>
            </div>

            <Field label="Başlık" hint="Örn: Proje görüşmesi, kahve buluşması, röportaj daveti">
              <Input value={form.title} onChange={(e) => upd("title", e.target.value)}
                placeholder="Randevu başlığı" data-testid="appt-title" />
            </Field>

            <Field label="E-posta Adresleri" hint="Birden fazla kişiye aynı anda gönderebilirsiniz">
              <div className="space-y-2" data-testid="appt-emails">
                {form.emails.map((em, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input type="email" value={em}
                      onChange={(e) => updList("emails", i, e.target.value)}
                      placeholder={i === 0 ? "ornek@eposta.com" : "ek@eposta.com"}
                      data-testid={`appt-email-${i}`} />
                    {form.emails.length > 1 && (
                      <button type="button" onClick={() => removeItem("emails", i)}
                        className="p-2 text-stone-400 hover:text-red-600" data-testid={`appt-email-rm-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {form.emails.length < 10 && (
                  <button type="button" onClick={() => addItem("emails")}
                    className="text-xs text-stone-600 hover:text-amber-700 inline-flex items-center gap-1 py-1"
                    data-testid="appt-email-add">
                    <Plus size={13} /> Yeni e-posta ekle
                  </button>
                )}
              </div>
            </Field>

            <Field label="Telefonlar" hint="İsteğe bağlı; birden fazla ekleyebilirsiniz">
              <div className="space-y-2" data-testid="appt-phones">
                {form.phones.map((ph, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input type="tel" value={ph}
                      onChange={(e) => updList("phones", i, e.target.value)}
                      placeholder="+90..." data-testid={`appt-phone-${i}`} />
                    {form.phones.length > 1 && (
                      <button type="button" onClick={() => removeItem("phones", i)}
                        className="p-2 text-stone-400 hover:text-red-600" data-testid={`appt-phone-rm-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {form.phones.length < 10 && (
                  <button type="button" onClick={() => addItem("phones")}
                    className="text-xs text-stone-600 hover:text-amber-700 inline-flex items-center gap-1 py-1"
                    data-testid="appt-phone-add">
                    <Plus size={13} /> Yeni telefon ekle
                  </button>
                )}
              </div>
            </Field>

            <Field label="Randevu Nedeni" hint="Zengin metin — başlıklar, listeler, link ve resim ekleyebilirsiniz">
              <SingleRichEditor
                value={form.reason}
                onChange={(h) => upd("reason", h)}
                testid="appt-reason"
                minH="min-h-[200px]"
              />
            </Field>

            <Field label="Kendi notunuz (yalnız size görünür)">
              <Input value={form.note} onChange={(e) => upd("note", e.target.value)} data-testid="appt-note" />
            </Field>

            {confirmClose && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm flex items-center justify-between gap-3">
                <span>Doldurulan alanlar kaybolacak. Çıkmak istediğinize emin misiniz?</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmClose(false)}>İptal</Button>
                  <Button variant="destructive" size="sm" onClick={onClose} data-testid="appt-confirm-close">Çık</Button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={openPreview} disabled={busy} data-testid="appt-preview">
                <Eye size={14} className="mr-1" /> Önizleme
              </Button>
              <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="appt-save-draft">
                <Save size={14} className="mr-1" /> Taslağa Kaydet
              </Button>
              <Button onClick={sendNow} disabled={busy} data-testid="appt-submit">
                <Send size={14} className="mr-1" /> {editingDraft ? "Taslağı Gönder" : "Talep Et"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
